/**
 * Booking Validation Hook
 * 
 * React Query hook for validating bookings with subscription limits.
 * 
 * ## Features
 * - Validates booking against subscription limits
 * - Calculates overage charges if limits exceeded
 * - Type-safe query with ApiResult handling
 * - Automatic caching and refetching
 * 
 * @module booking/hooks
 */

import { useQuery } from '@tanstack/react-query';
import { queryKeys } from '~/lib/query/keys';
import { cachePresets } from '~/lib/query/cachePresets';
import { bookingService } from '../api/bookingService';

export interface UseBookingValidationOptions {
  /** Club ID */
  clubId: number | null | undefined;
  
  /** Bay ID */
  bayId: number | null | undefined;
  
  /** Start time (ISO 8601 string) */
  startTime: string | null | undefined;
  
  /** End time (ISO 8601 string) */
  endTime: string | null | undefined;
  
  /** User ID (optional - for subscription validation) */
  userId?: number | null | undefined;

  /** Number of guests (excludes the booker). Used for guest fee calculation. */
  guestCount?: number;

  /** Priced STRICTLY here — this path has an identity, so the per-user rules run. */
  promoCode?: string;

  /** Whether the query is enabled (default: true if required params exist) */
  enabled?: boolean;
}

/**
 * Validate booking with subscription limits
 * 
 * Returns the full React Query result with a convenience `validation` field.
 * 
 * @example
 * ```tsx
 * // Simple usage - convenience field
 * const { validation, isLoading } = useBookingValidation({
 *   clubId: 1,
 *   bayId: 2,
 *   startTime: '2025-12-22T10:00:00.000Z',
 *   endTime: '2025-12-22T11:00:00.000Z',
 *   userId: authUser?.id,
 * });
 * 
 * if (validation?.overage?.hasOverage) {
 *   console.log('Overage cost:', validation.overageCost?.amount);
 * }
 * 
 * // Advanced usage - access React Query features  
 * const { validation, isFetching, refetch } = useBookingValidation({...});
 * ```
 */
export function useBookingValidation(options: UseBookingValidationOptions) {
  const {
    clubId,
    bayId,
    startTime,
    endTime,
    userId,
    guestCount,
    promoCode,
    enabled = true,
  } = options;

  // Ensure all required values exist before using them
  const hasRequiredParams = !!clubId && !!bayId && !!startTime && !!endTime;

  const query = useQuery({
    queryKey: hasRequiredParams
      ? queryKeys.bookings.validation(clubId, { 
          bayId,
          startTime,
          endTime,
          userId: userId ?? undefined,
          guestCount,
          promoCode,
        })
      : queryKeys.disabled.bookingValidation(),
    queryFn: async () => {
      // Type guard: We know these exist because hasRequiredParams is checked in enabled
      if (!clubId || !bayId || !startTime || !endTime) {
        throw new Error('Required parameters missing');
      }

      const result = await bookingService.validateBooking({
        clubId,
        bayId,
        startTime,
        endTime,
        userId: userId || undefined,
        guestCount,
        promoCode,
      });

      if (!result.success) {
        throw new Error(result.message || 'Booking validation failed');
      }

      return result.payload;
    },
    enabled: enabled && hasRequiredParams,
    ...cachePresets.frequent,
  });

  return {
    ...query,
    validation: query.data ?? null,
  };
}
